import cn from "classnames";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";

import {
  InvestObjectParametersProps,
  InvestObjectParametersFormProps,
} from "./InvestObjectParameters.types";

import {
  CALCULATOR_TYPES,
  sonderAfaForcedOptions,
  getGrowthDynamicOptions,
  priceGrowthRelationOptions,
} from "constants/calculator";

import {
  visibleFields,
  disabledFields,
  defaultFieldProps,
  defaultFieldGroupProps,
} from "./InvestObjectParameters.config";

import { checkFieldConfiguration } from "helpers/checkFieldConfiguration";
import { checkIfPriceGrowthRelationTypeDisabled } from "./InvestObjectParameters.helpers";

import { Typography } from "shared/ui/components/Typography";

import {
  InputField,
  SelectField,
  MonthPickerInputField,
  ButtonGroupField,
} from "components/FormikFields";
import { CalculatorFormikField } from "components/CalculatorFormikField";

import { FieldGroup } from "../FieldGroup";

import styles from "./InvestObjectParameters.module.scss";
import { FormikField } from "components/FormikField";

const Fields = ({
  isMobile = false,
  t,
  isRootAdmin,
  defaultDowntimeDynamicNotEmpty,
  calculatorType,
  loansOptions,
  onEnergyEfficiencyClassChange,
  onPriceGrowthRelationTypeChange,
  onOrderDateChange,
  onFinishDateChange,
}: InvestObjectParametersFormProps) => {
  const { values } = useFormikContext<any>();

  return (
    <div className={styles.fields}>
      <div
        className={cn(styles.fieldsColumn, {
          [styles.fieldsColumnStandard]:
            calculatorType === CALCULATOR_TYPES.BASIC,
        })}
      >
        <div className={cn(styles.header, { [styles.proHeader]: calculatorType === CALCULATOR_TYPES.STANDARD || calculatorType === CALCULATOR_TYPES.EXPERT || calculatorType === CALCULATOR_TYPES.LIMITED_PRO })}>
          <Typography
            className={styles.headerName}
            variant="bodyS"
            component="span"
          >
            {t("Allgemeine Parameter")}
          </Typography>
        </div>
        <CalculatorFormikField
          name={`brokerFee`}
          hidden={checkFieldConfiguration(
            calculatorType,
            visibleFields.brokerFee
          )}
          component={InputField}
          controlProps={{
            ...defaultFieldProps,
            label: t("calculator_form.invest_object_parameters.broker_fee"),
          }}
          componentProps={{
            type: "localeDe",
            disabled: !checkFieldConfiguration(
              calculatorType,
              disabledFields.brokerFee
            ),
            placeholder: "0",
            endAdornment: <Typography className={styles.currency} variant="bodyM" weight="medium">%</Typography>,
            withoutBorder: true,
            fieldClassName: styles.input,
            className: styles.inputWrapper
          }}
        />

        <CalculatorFormikField
          name={`propertyTax`}
          hidden={checkFieldConfiguration(
            calculatorType,
            visibleFields.propertyTax
          )}
          component={InputField}
          controlProps={{
            ...defaultFieldProps,
            label: t("calculator_form.invest_object_parameters.property_tax"),
          }}
          componentProps={{
            type: "localeDe",
            disabled: !checkFieldConfiguration(
              calculatorType,
              disabledFields.propertyTax
            ),
            placeholder: "0",
            endAdornment: <Typography className={styles.currency} variant="bodyM" weight="medium">%</Typography>,
            withoutBorder: true,
            fieldClassName: styles.input,
            className: styles.inputWrapper
          }}
        />

        <CalculatorFormikField
          name={`energyEfficiencyClass`}
          hidden={checkFieldConfiguration(
            calculatorType,
            visibleFields.energyEfficiencyClass
          )}
          component={SelectField}
          controlProps={{
            ...defaultFieldProps,
            size: "small",
            label: t(
              "calculator_form.invest_object_parameters.building_standard.title"
            ),
          }}
          componentProps={{
            disabled: !checkFieldConfiguration(
              calculatorType,
              disabledFields.energyEfficiencyClass
            ),
            placeholder: t(
              "calculator_form.invest_object_parameters.building_standard.none"
            ),
            size: "small",
            options: loansOptions,
            onChanged: (option: any) => {
              onEnergyEfficiencyClassChange(option);
            },
            withoutBorder: true,
            controlClassName: styles.selectField
          }}
        />

        <CalculatorFormikField
          name={`isSonderAfaForced`}
          hidden={checkFieldConfiguration(
            calculatorType,
            visibleFields.sonderAfaForced
          )}
          component={SelectField}
          controlProps={{
            ...defaultFieldProps,
            size: "small",
            label: t("calculator_form.invest_object_parameters.sonder_afa"),
          }}
          componentProps={{
            disabled:
              !checkFieldConfiguration(
                calculatorType,
                disabledFields.sonderAfaForced
              ) || values.energyEfficiencyClass?.value !== "QNG",
            size: "small",
            options: sonderAfaForcedOptions,
            withoutBorder: true,
            controlClassName: styles.selectField
          }}
        />
        <CalculatorFormikField
          name={`stages[0].date`}
          component={MonthPickerInputField}
          controlProps={{
            ...defaultFieldProps,
            label: t("calculator_form.invest_object_parameters.start_date"),
          }}
          hidden={checkFieldConfiguration(
            calculatorType,
            visibleFields.startDate
          )}
          componentProps={{
            fieldDisabled: !checkFieldConfiguration(
              calculatorType,
              disabledFields.startDate
            ),
            disabled: !checkFieldConfiguration(
              calculatorType,
              disabledFields.startDate
            ),
            withoutBorder: true,
            fieldClassName: styles.input,
            className: styles.inputWrapper,
            component: "div"
          }}
        />
        <CalculatorFormikField
          name={`stages[1].date`}
          component={MonthPickerInputField}
          controlProps={{
            ...defaultFieldProps,
            label: t("calculator_form.invest_object_parameters.purchase_date"),
          }}
          hidden={checkFieldConfiguration(
            calculatorType,
            visibleFields.orderDate
          )}
          componentProps={{
            onChanged: (_, value: any) => onOrderDateChange(value),
            withoutBorder: true,
            fieldClassName: styles.input,
            className: styles.inputWrapper,
            component: "div"
          }}
        />

        <CalculatorFormikField
          name={`stages[2].date`}
          hidden={checkFieldConfiguration(
            calculatorType,
            visibleFields.finishDate
          )}
          component={MonthPickerInputField}
          controlProps={{
            ...defaultFieldProps,
            label: t("calculator_form.invest_object_parameters.finish_date"),
          }}
          componentProps={{
            fieldDisabled: !checkFieldConfiguration(
              calculatorType,
              disabledFields.finishDate
            ),
            disabled: !checkFieldConfiguration(
              calculatorType,
              disabledFields.finishDate
            ),
            onChanged: (_, value: any) => onFinishDateChange(value),
            withoutBorder: true,
            fieldClassName: styles.input,
            className: styles.inputWrapper,
            component: "div"
          }}
        />

        <div className={cn(styles.header, { [styles.proHeader]: calculatorType === CALCULATOR_TYPES.STANDARD || calculatorType === CALCULATOR_TYPES.EXPERT || calculatorType === CALCULATOR_TYPES.LIMITED_PRO })}>
          <Typography
            className={styles.headerName}
            variant="bodyS"
            component="span"
          >
            {t("Hausverwaltung")}
          </Typography>
        </div>

        <CalculatorFormikField
          name={`stages[3].date`}
          hidden={checkFieldConfiguration(
            calculatorType,
            visibleFields.rentStartDate
          )}
          component={MonthPickerInputField}
          controlProps={{
            ...defaultFieldProps,
            label: t(
              "calculator_form.invest_object_parameters.rent_start_date"
            ),
            withoutBorder: true,
            controlClassName: styles.selectField
          }}
          componentProps={{
            withoutBorder: true,
            fieldClassName: styles.input,
            className: styles.inputWrapper,
            component: "div"
          }}
        />
      </div>
      <div
        className={cn(styles.fieldsColumn, {
          [styles.fieldsColumnHidden]:
            calculatorType === CALCULATOR_TYPES.BASIC,
        })}
      >
        <CalculatorFormikField
          name={`managementCost`}
          hidden={checkFieldConfiguration(
            calculatorType,
            visibleFields.managementCost
          )}
          component={InputField}
          controlProps={{
            ...defaultFieldProps,
            label: t(
              "calculator_form.invest_object_parameters.management_cost"
            ),
          }}
          componentProps={{
            type: "localeDe",
            placeholder: "0",
            endAdornment: <Typography className={styles.currency} variant="bodyM" weight="medium">€</Typography>,
            withoutBorder: true,
            fieldClassName: styles.input,
            className: styles.inputWrapper
          }}
        />

        <FieldGroup
          label={t(
            "calculator_form.invest_object_parameters.management_cost_growth_rate"
          )}
          hidden={checkFieldConfiguration(
            calculatorType,
            visibleFields.managementCostGrowth
          )}
          className={styles.fieldGroup}
          labelClassName={cn(styles.fieldGroupLabel,
            {
              [styles.fieldGroupLabelSingle]: isMobile
            }
          )}
        >
          <FormikField
            name={`managementCostGrowth`}
            component={InputField}
            withoutError={true}
            controlProps={{
              ...defaultFieldGroupProps,
              ...(isMobile && { className: styles.fieldGroupControlSingle }),
            }}
            componentProps={{
              type: "localeDe",
              placeholder: "0",
              endAdornment: <Typography className={styles.currency} variant="bodyM" weight="medium">%</Typography>,
              withoutBorder: true,
              fieldClassName: styles.input,
              className: styles.inputWrapper
            }}
          />
          {!isMobile && <FormikField
            name={`managementCostGrowthDynamic`}
            component={SelectField}
            controlProps={defaultFieldGroupProps}
            componentProps={{
              size: "small",
              options: getGrowthDynamicOptions(t),
              withoutBorder: true,
              controlClassName: styles.selectField,
              menuClassName: styles.selectFieldMenu
            }}
          />}
        </FieldGroup>
        {isMobile &&
          <FormikField
            hidden={checkFieldConfiguration(
              calculatorType,
              visibleFields.managementCostGrowth
            )}
            name={`managementCostGrowthDynamic`}
            component={ButtonGroupField}
            controlProps={defaultFieldGroupProps}
            componentProps={{
              type: "field",
              list: getGrowthDynamicOptions(t),
              className: styles.growthDynamicButton,
              activeClassName: styles.growthDynamicButtonActive,
              size: "small"
            }}
          />
        }
        <div className={cn(styles.header, { [styles.proHeader]: calculatorType === CALCULATOR_TYPES.STANDARD || calculatorType === CALCULATOR_TYPES.EXPERT || calculatorType === CALCULATOR_TYPES.LIMITED_PRO })}>
          <Typography
            className={styles.headerName}
            variant="bodyS"
            component="span"
          >
            {t("Instandhaltungskosten")}
          </Typography>
        </div>


        <CalculatorFormikField
          name={`maintenanceCostPerSqMeter`}
          hidden={checkFieldConfiguration(
            calculatorType,
            visibleFields.maintenanceCostPerSqMeter
          )}
          component={InputField}
          controlProps={{
            ...defaultFieldProps,
            label: t(
              "calculator_form.invest_object_parameters.maintenance_cost_per_sq_meter"
            ),
          }}
          componentProps={{
            type: "localeDe",
            placeholder: "0",
            endAdornment: <Typography className={styles.currency} variant="bodyM" weight="medium">€</Typography>,
            withoutBorder: true,
            fieldClassName: styles.input,
            className: styles.inputWrapper
          }}
        />

        <FieldGroup
          label={t(
            "calculator_form.invest_object_parameters.maintenance_cost_per_sq_meter_growth_rate"
          )}
          hidden={checkFieldConfiguration(
            calculatorType,
            visibleFields.maintenanceCostPerSqMeterGrowth
          )}
          className={styles.fieldGroup}
          labelClassName={cn(styles.fieldGroupLabel,
            {
              [styles.fieldGroupLabelSingle]: isMobile
            }
          )}
        >
          <FormikField
            name={`maintenanceCostPerSqMeterGrowth`}
            component={InputField}
            withoutError={true}
            controlProps={{
              ...defaultFieldGroupProps,
              ...(isMobile && { className: styles.fieldGroupControlSingle })
            }}
            componentProps={{
              type: "localeDe",
              placeholder: "0",
              endAdornment: <Typography className={styles.currency} variant="bodyM" weight="medium">%</Typography>,
              withoutBorder: true,
              fieldClassName: styles.input,
              className: styles.inputWrapper
            }}
          />
          {!isMobile && <FormikField
            name={`maintenanceCostPerSqMeterGrowthDynamic`}
            component={SelectField}
            controlProps={defaultFieldGroupProps}
            componentProps={{
              size: "small",
              options: getGrowthDynamicOptions(t),
              withoutBorder: true,
              controlClassName: styles.selectField,
              menuClassName: styles.selectFieldMenu
            }}
          />}
        </FieldGroup>
        {isMobile &&
          <FormikField
            hidden={checkFieldConfiguration(
              calculatorType,
              visibleFields.maintenanceCostPerSqMeterGrowth
            )}
            name={`maintenanceCostPerSqMeterGrowthDynamic`}
            component={ButtonGroupField}
            controlProps={defaultFieldGroupProps}
            componentProps={{
              type: "field",
              list: getGrowthDynamicOptions(t),
              className: styles.growthDynamicButton,
              activeClassName: styles.growthDynamicButtonActive,
              size: "small"
            }}
          />
        }

        <div className={cn(styles.header, { [styles.proHeader]: calculatorType === CALCULATOR_TYPES.STANDARD || calculatorType === CALCULATOR_TYPES.EXPERT || calculatorType === CALCULATOR_TYPES.LIMITED_PRO })}>
          <Typography
            className={styles.headerName}
            variant="bodyS"
            component="span"
          >
            {t("Leerstand durchschnittlich")}
          </Typography>
        </div>

        <FieldGroup
          label={t(
            "calculator_form.invest_object_parameters.downtime_percentage"
          )}
          hidden={checkFieldConfiguration(
            calculatorType,
            visibleFields.downtimePercentage
          )}
          className={styles.fieldGroup}
          labelClassName={cn(styles.fieldGroupLabel,
            {
              [styles.fieldGroupLabelSingle]: (!isRootAdmin && !defaultDowntimeDynamicNotEmpty) || isMobile
            }
          )}
        >
          <FormikField
            name={`downtimePercentage`}
            component={InputField}
            withoutError={true}
            controlProps={{
              ...defaultFieldGroupProps,
              ...(((!isRootAdmin && !defaultDowntimeDynamicNotEmpty) || isMobile) && {
                className: styles.fieldGroupControlSingle,
              }),
            }}
            componentProps={{
              type: "localeDe",
              placeholder: "0",
              endAdornment: <Typography className={styles.currency} variant="bodyM" weight="medium">%</Typography>,
              withoutBorder: true,
              fieldClassName: styles.input,
              className: styles.inputWrapper
            }}
          />
          {(isRootAdmin || defaultDowntimeDynamicNotEmpty) && !isMobile && (
            <FormikField
              name={`downtimePercentageDynamic`}
              component={SelectField}
              controlProps={defaultFieldGroupProps}
              componentProps={{
                size: "small",
                options: getGrowthDynamicOptions(t),
                withoutBorder: true,
                controlClassName: styles.selectField
              }}
            />
          )}
        </FieldGroup>
        {(isRootAdmin || defaultDowntimeDynamicNotEmpty) && isMobile &&
          <FormikField
            hidden={checkFieldConfiguration(
              calculatorType,
              visibleFields.maintenanceCostPerSqMeterGrowth
            )}
            name={`downtimePercentageDynamic`}
            component={ButtonGroupField}
            controlProps={defaultFieldGroupProps}
            componentProps={{
              type: "field",
              list: getGrowthDynamicOptions(t),
              className: styles.growthDynamicButton,
              activeClassName: styles.growthDynamicButtonActive,
              size: "small"
            }}
          />
        }

        {calculatorType !== CALCULATOR_TYPES.BASIC && (
          <>
            {calculatorType === CALCULATOR_TYPES.LIMITED_PRO && (
              <div className={cn(styles.header, styles.proHeader)}>
                <Typography
                  className={styles.headerName}
                  variant="bodyS"
                  component="span"
                >
                  {t("Beginn der Wertsteigerung")}
                </Typography>
              </div>
            )}
            <CalculatorFormikField
              name={`priceGrowth.relationType`}
              component={SelectField}
              hidden={checkFieldConfiguration(
                calculatorType,
                visibleFields.priceGrowthRelationType
              )}
              controlProps={{
                ...defaultFieldProps,
                label: "Beginn der Wertsteigerung"
              }}
              componentProps={{
                size: "small",
                options: priceGrowthRelationOptions(t),
                onChanged: onPriceGrowthRelationTypeChange,
                disabled: checkIfPriceGrowthRelationTypeDisabled(values),
                withoutBorder: true,
                controlClassName: styles.selectField
              }}
            />
            <CalculatorFormikField
              name={`priceGrowth.date`}
              component={MonthPickerInputField}
              controlProps={{
                ...defaultFieldProps,
                label: "Beginn der Wertsteigerung"
              }}
              hidden={checkFieldConfiguration(
                calculatorType,
                visibleFields.priceGrowthDate
              )}
              componentProps={{
                fieldDisabled:
                  !checkFieldConfiguration(
                    calculatorType,
                    disabledFields.priceGrowthDate
                  ) || values.priceGrowth.relationType.value !== "CUSTOM",
                disabled:
                  !checkFieldConfiguration(
                    calculatorType,
                    disabledFields.priceGrowthDate
                  ) || values.priceGrowth.relationType.value !== "CUSTOM",
                withoutBorder: true,
                fieldClassName: styles.input,
                className: styles.inputWrapper,
                component: "div"
              }}
            />
          </>
        )}
      </div>
    </div>
  );
};

const InvestObjectParametersDesktop = ({
  t,
  isRootAdmin,
  calculatorType,
  defaultDowntimeDynamicNotEmpty,
  loansOptions,
  onOrderDateChange,
  onFinishDateChange,
  onEnergyEfficiencyClassChange,
  onPriceGrowthRelationTypeChange,
}: InvestObjectParametersFormProps) => {
  return (
    <div className={styles.rootDesktop}>
      <Fields
        t={t}
        isRootAdmin={isRootAdmin}
        defaultDowntimeDynamicNotEmpty={defaultDowntimeDynamicNotEmpty}
        calculatorType={calculatorType}
        loansOptions={loansOptions}
        onOrderDateChange={onOrderDateChange}
        onFinishDateChange={onFinishDateChange}
        onEnergyEfficiencyClassChange={onEnergyEfficiencyClassChange}
        onPriceGrowthRelationTypeChange={onPriceGrowthRelationTypeChange}
      />
    </div>
  );
};

const InvestObjectParametersMobile = ({
  t,
  isRootAdmin,
  calculatorType,
  loansOptions,
  defaultDowntimeDynamicNotEmpty,
  onOrderDateChange,
  onFinishDateChange,
  onEnergyEfficiencyClassChange,
  onPriceGrowthRelationTypeChange,
}: InvestObjectParametersFormProps) => {
  return (
    <div className={styles.rootMobile}>
      <div className={cn(styles.header, { [styles.standardHeader]: calculatorType === CALCULATOR_TYPES.BASIC })}>
        <Typography
          className={styles.headerName}
          variant="bodyS"
          component="span"
        >
          {t("calculator_form.invest_object_parameters.title")}
        </Typography>
      </div>
      <Fields
        t={t}
        isRootAdmin={isRootAdmin}
        calculatorType={calculatorType}
        loansOptions={loansOptions}
        onOrderDateChange={onOrderDateChange}
        onFinishDateChange={onFinishDateChange}
        defaultDowntimeDynamicNotEmpty={defaultDowntimeDynamicNotEmpty}
        onEnergyEfficiencyClassChange={onEnergyEfficiencyClassChange}
        onPriceGrowthRelationTypeChange={onPriceGrowthRelationTypeChange}
        isMobile={true}
      />
    </div>
  );
};

const InvestObjectParameters = ({
  activeTab,
  isDesktop,
  isRootAdmin,
  defaultDowntimeDynamicNotEmpty,
  calculatorType,
  loansOptions,
  onOrderDateChange,
  onFinishDateChange,
  onEnergyEfficiencyClassChange,
  onPriceGrowthRelationTypeChange,
}: InvestObjectParametersProps) => {
  const { t } = useTranslation();

  if (
    calculatorType !== CALCULATOR_TYPES.BASIC &&
    activeTab !== "investObjectParameters"
  ) {
    return null;
  }

  if (!isDesktop) {
    return (
      <InvestObjectParametersMobile
        t={t}
        isRootAdmin={isRootAdmin}
        defaultDowntimeDynamicNotEmpty={defaultDowntimeDynamicNotEmpty}
        calculatorType={calculatorType}
        loansOptions={loansOptions}
        onOrderDateChange={onOrderDateChange}
        onFinishDateChange={onFinishDateChange}
        onEnergyEfficiencyClassChange={onEnergyEfficiencyClassChange}
        onPriceGrowthRelationTypeChange={onPriceGrowthRelationTypeChange}
      />
    );
  }

  return (
    <InvestObjectParametersDesktop
      t={t}
      isRootAdmin={isRootAdmin}
      defaultDowntimeDynamicNotEmpty={defaultDowntimeDynamicNotEmpty}
      calculatorType={calculatorType}
      loansOptions={loansOptions}
      onOrderDateChange={onOrderDateChange}
      onFinishDateChange={onFinishDateChange}
      onEnergyEfficiencyClassChange={onEnergyEfficiencyClassChange}
      onPriceGrowthRelationTypeChange={onPriceGrowthRelationTypeChange}
    />
  );
};

export default InvestObjectParameters;
